import React, { useEffect } from "react"
import styled, { css } from "styled-components"

import Badge from "~/components/atoms/Badge"
import Link from "~/components/atoms/Link"
import Label from "~/components/atoms/Tag"
import { Color, SpacingSize, TextSize, Transition } from "~/consts/style-mixins"

interface Props {
  items: ItemType[]
  contents?: React.ReactNode[]
  size: keyof typeof SizeType
  selectedTabIndex?: number
  full?: boolean
  vertical?: boolean
  className?: string
  contentsClassName?: string
}

interface ItemType {
  name: string
  href?: string
  dynamicRoutePath?: string
  hasLabel?: boolean
  labelName?: string
  notificationCount?: number
}

interface StyledProps {
  styleBySize?: string
  eachItemWidth?: number | null
  selected?: boolean
  vertical?: boolean
  full?: boolean
  show?: boolean
}

export const SizeType = {
  Small: "Small",
  Medium: "Medium",
  Large: "Large",
}

const { useState } = React

const Tab = ({ items, contents, size, selectedTabIndex, full, vertical, className, contentsClassName }: Props) => {
  const [selected, setSelectTab] = useState(selectedTabIndex)

  useEffect(() => {
    setSelectTab(selectedTabIndex)
  }, [selectedTabIndex])

  return (
    <>
      <StyledTabItems vertical={vertical} className={className}>
        {items.map((item: ItemType, index) => {
          return (
            <StyledTabItem
              key={index}
              eachItemWidth={full ? 100 / items.length : null}
              vertical={vertical}
              selected={selected ? selected - 1 === index : false}
              onClick={() => setSelectTab(index + 1)}
            >
              {item.href ? (
                <StyledTabButton
                  as={Link}
                  styleBySize={styleBySize[size]}
                  vertical={vertical}
                  href={item.href}
                  dynamicRoutePath={item.dynamicRoutePath}
                >
                  {item.name}
                </StyledTabButton>
              ) : (
                <StyledTabButton styleBySize={styleBySize[size]} vertical={vertical}>
                  {item.name}
                  {Boolean(item.notificationCount) && <StyledTabBadge count={item.notificationCount} />}
                </StyledTabButton>
              )}
              {item.hasLabel && (
                <div className="label-wrapper">
                  <Label size="Small" className="label" color={Color.Attention}>
                    {item.labelName}
                  </Label>
                </div>
              )}
            </StyledTabItem>
          )
        })}
      </StyledTabItems>
      <div className={contentsClassName}>
        {contents &&
          contents.map((content, index) => {
            return (
              <StyledTabContent key={index} show={selected ? selected - 1 === index : index === 0}>
                {content}
              </StyledTabContent>
            )
          })}
      </div>
    </>
  )
}

const styleBySize = {
  Small: `
    padding: ${SpacingSize.S}px 4px;
    font-size: ${TextSize.S}px;
  `,
  Medium: `
    padding: ${SpacingSize.S}px 6px;
    font-size: ${TextSize.S}px;
  `,
  Large: `
    padding: ${SpacingSize.M}px ${SpacingSize.XS}px;
    font-size: ${TextSize.M}px;
  `,
}

const StyledTabItems = styled.div`
  width: 100%;
  display: flex;
  background-color: ${Color.White};
  ${({ vertical }: StyledProps) => css`
    ${vertical
      ? `
        flex-direction: column;
    `
      : `
        border-bottom: 1px solid ${Color.BorderGray};
    `}
  `}
`

const StyledTabItem = styled.div`
  position: relative;
  color: ${Color.LightGray};
  ${Transition.Hover};
  border-bottom: 2px solid ${Color.White};
  ${({ selected, vertical, eachItemWidth }: StyledProps) => css`
    ${selected &&
    `
      color: ${Color.Primary};
      font-weight: bold;
    `}
    ${!vertical &&
    !eachItemWidth &&
    `
      white-space: nowrap;
    `}
    ${vertical &&
    selected &&
    css`
      background-color: ${Color.BackgroundGray};
      border-radius: 8px;
    `}
    ${!vertical &&
    selected &&
    css`
      border-bottom: 2px solid ${Color.Primary};
    `}
  `}
  ${({ eachItemWidth }: StyledProps) => css`
    ${eachItemWidth &&
    css`
      width: ${eachItemWidth}%;
      text-align: center;
    `}
  `}
  &:hover {
    color: ${Color.Primary};
  }
  .label-wrapper {
    position: absolute;
    top: -4px;
    left: 2px;
  }
  .label {
    display: inline;
    padding: 1px ${SpacingSize["2XS"]}px;
  }
`
const StyledTabButton = styled.div`
  display: block;
  cursor: pointer;
  ${({ styleBySize }: StyledProps) => css`
    ${styleBySize}
  `}
  ${({ vertical }: StyledProps) => css`
    ${vertical &&
    css`
      padding: ${SpacingSize.M}px;
    `}
  `}
  ${({ full }: StyledProps) => css`
    ${full &&
    css`
      text-align: center;
    `}
  `}
`

const StyledTabBadge = styled(Badge)`
  position: relative;
  top: -1px;
  left: 4px;
  height: 18px;
  min-width: 18px;
`

const StyledTabContent = styled.div`
  ${({ show }: StyledProps) =>
    !show &&
    css`
      opacity: 0;
      pointer-events: none;
      height: 0;
      overflow: hidden;
    `}
`

export default React.memo(Tab)
